// crewbit-admin/src/hooks/useDownloadMoneyExport.ts
//
// Session 137 §Settings hub substrate — generate-money-export edge function
// wrapper for admin (browser). Spec:
// docs/references/MONEY_MODULE_SPEC_v1.md §Settings.5 + §Settings.D6.
//
// Web-runtime path per §3d + failure mode #10: triggers browser download via
// Blob + <a download> click. Mobile uses expo-sharing instead — different
// shape, same edge fn underneath.
//
// Read-only — does NOT emit MONEY_MUTATION_EVENT (no mutation; cache
// invalidation not warranted).

import { useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { MoneyErrorCode } from '../lib/errorMap';

export type MoneyExportType = 't2125' | 'hst_summary' | 'payouts_log';

export interface DownloadMoneyExportArgs {
  businessId: string;
  exportType: MoneyExportType;
}

export type DownloadMoneyExportResult =
  | { success: true; filename: string }
  | { success: false; error: { code: MoneyErrorCode | 'UNKNOWN'; message?: string } };

export function useDownloadMoneyExport() {
  return useCallback(
    async (args: DownloadMoneyExportArgs): Promise<DownloadMoneyExportResult> => {
      try {
        const { data, error } = await supabase.functions.invoke<string>(
          'generate-money-export',
          {
            body: { business_id: args.businessId, export_type: args.exportType },
          },
        );

        if (error) {
          // Edge fn jsonError responses: {success:false,error:{code,message}}.
          // supabase-js exposes the parsed body via FunctionsHttpError.context.
          const ctxBody = (error as unknown as { context?: { body?: unknown } }).context?.body;
          if (ctxBody && typeof ctxBody === 'object' && 'error' in ctxBody) {
            const errPayload = (ctxBody as { error: { code: string; message?: string } }).error;
            return {
              success: false,
              error: {
                code: (errPayload.code as MoneyErrorCode) ?? 'UNKNOWN',
                message: errPayload.message,
              },
            };
          }
          return { success: false, error: { code: 'UNKNOWN', message: error.message } };
        }

        // Success: trigger browser download via Blob + <a download> click.
        const year = new Date().getFullYear();
        const filename =
          args.exportType === 't2125'
            ? `t2125_${year}.csv`
            : args.exportType === 'hst_summary'
              ? `hst_summary_${year}.csv`
              : `${args.exportType}_${year}.csv`;

        const blob = new Blob([data ?? ''], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        return { success: true, filename };
      } catch (e) {
        return {
          success: false,
          error: {
            code: 'UNKNOWN',
            message: e instanceof Error ? e.message : 'Failed to download export',
          },
        };
      }
    },
    [],
  );
}
